import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { DeveloperService } from '../services/developer.service';
import { DocumentTypeClassifier } from '../models/document-type-classifier';
import { CurrentUserService } from '../../user/services';
import { DocumentTypeEntity } from '../../distribution/models/distribution-object';
import { IdentificationType } from '../../partner/models';
import { PartnerIdentificationTypeService } from '../../partner/services/partner-identification-type.service';
import { Direction, DocumentConfigSearchDisplay } from '../../distribution/models';
import { DocumentConfigSearch, DocumentConfigService } from '../../distribution/services/document-config.service';
import { ToastService, ToastType } from '../../shared/toast/toast.service';
import { DataFieldGroup } from '../../shared/datafields/models';
import { DocumentTypeClassifierResolverEntity } from '../models/document-type-classifier-resolver';
import { UntypedFormGroup, FormsModule } from '@angular/forms';
import { ArtifactAssemblyTypes, AssemblyArtifact } from '../models/artifact';
import { DeveloperArtifactService } from '../services/developer-artifact.service';
import { GridService } from '../../shared/grid/grid.service';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { DxDataGridModule } from 'devextreme-angular';
import { BizDataGridDirective } from '../../shared/grid/biz-grid.directive';
import { DxoEditingModule, DxoPopupModule, DxoFormModule, DxiItemModule, DxoColumnFixingModule, DxiColumnModule, DxoLookupModule, DxiButtonModule } from 'devextreme-angular/ui/nested';
import { BizUserColumnDirective } from '../../shared/grid/biz-grid-user.directive';
import { BizDateColumnDirective } from '../../shared/grid/biz-grid-date.directive';
import { DxTemplateModule } from 'devextreme-angular/core';

import { DataFieldGroupContainerComponent } from '../../shared/datafields/datafield-group-container.component';
import { DeveloperArtifactAssemblyPickerComponent } from '../developer-artifacts/developer-artifact-assembly-picker.component';
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';
import { DxDataGridTypes } from 'devextreme-angular/ui/data-grid';

@Component({
    selector: 'app-developer-document-type-classifier',
    templateUrl: './developer-document-type-classifier.component.html',
    styleUrls: ['./developer-document-type-classifier.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxoPopupModule, DxoFormModule, DxiItemModule, DxoColumnFixingModule, DxiColumnModule, DxoLookupModule, BizUserColumnDirective, BizDateColumnDirective, DxiButtonModule, DxTemplateModule, FormsModule, DataFieldGroupContainerComponent, DeveloperArtifactAssemblyPickerComponent]
})
export class DeveloperDocumentTypeClassifiersComponent implements OnInit {

    hasCreatePerm = false;
    hasEditPerm = false;
    hasDeletePerm = false;
    hasShowLogPerm = false;

    documentTypes: DocumentTypeEntity[] = [];
    identificationTypes: IdentificationType[] = [];
    documentConfigs: DocumentConfigSearchDisplay[] = [];
    documentConfigsToDisplay: DocumentConfigSearchDisplay[] = [];

    documentTypeClassifierResolverFormGroup: UntypedFormGroup = new UntypedFormGroup({});
    controlMessageInResolverFormGroup: UntypedFormGroup = new UntypedFormGroup({});

    selectedDocumentTypeClassifierResolver: number;
    selectedDocumentType: number;
    selectedIdentificationType: number | null;
    selectedDocumentConfig: number | null;

    disableControlMessageInFlowOptions = true;
    clearArtifactPickerCurrentSelection = false;

    artifacts: AssemblyArtifact[] = [];
    documentTypeClassifiers: DocumentTypeClassifier[] = [];
    documentTypeClassifierResolvers: DocumentTypeClassifierResolverEntity[] = [];
    documentTypeClassifierResolverDataFields: DataFieldGroup[] = [];
    controlMessageInResolverDataFields: DataFieldGroup[] = [];

    constructor(
        private loadingPanelService: LoadingPanelService,
        private toastService: ToastService,
        private developerArtifactService: DeveloperArtifactService,
        private currentUserService: CurrentUserService,
        private documentConfigService: DocumentConfigService,
        private partnerIdentificationTypeService: PartnerIdentificationTypeService,
        private developerService: DeveloperService,
        private gridService: GridService,
        public bizGridFiltervalues: BizGridFiltervalues) {
            this.onEditorPreparing = this.onEditorPreparing.bind(this);
            this.setDocumentConfigValue = this.setDocumentConfigValue.bind(this);
    }

    async ngOnInit() {
        this.loadingPanelService.showLoadingPanelDirect('Loading document type classifers...');
        this.hasCreatePerm = this.currentUserService.hasPermission('Developer_Action_DocumentTypeClassifier_Create');
        this.hasEditPerm = this.currentUserService.hasPermission('Developer_Action_DocumentTypeClassifier_Edit');
        this.hasDeletePerm = this.currentUserService.hasPermission('Developer_Action_DocumentTypeClassifier_Delete');
        this.hasShowLogPerm = this.currentUserService.hasPermission('Developer_Action_ShowLog');
        this.artifacts = await this.developerArtifactService.getAssemblyArtifacts(true, [ArtifactAssemblyTypes.TypeControlMessageInResolver, ArtifactAssemblyTypes.TypeControlMessageInResolver2]);
        this.documentTypeClassifierResolvers = await this.developerService.getDocumentTypeClassifierResolvers();
        this.documentTypes = await this.developerService.getDocumentTypes();
        this.identificationTypes = await this.partnerIdentificationTypeService.getIdentificationTypes(false);
        this.documentConfigs = await this.documentConfigService.getDocumentConfigs(new DocumentConfigSearch(Direction.In));
        this.documentTypes.sort((a, b) => a.Name.localeCompare(b.Name));
        this.identificationTypes.sort((a, b) => a.Name.localeCompare(b.Name));
        this.loadingPanelService.hideLoadingPanel();
        this.refresh();
    }

    async refresh() {
        this.loadingPanelService.showLoadingPanelDirect('Loading document type classifers...');
        this.documentTypeClassifiers = await this.developerService.getDocumentTypeClassifiers();
        this.loadingPanelService.hideLoadingPanel();
    }

    async onEditingStart(e: any) {
        const documentTypeClassifier = e.data as DocumentTypeClassifier;
        this.selectedDocumentType = documentTypeClassifier.DocumentTypeId;
        this.selectedIdentificationType = documentTypeClassifier.IdentificationTypeId;
        this.selectedDocumentConfig = documentTypeClassifier.DocumentConfigId;
        this.selectedDocumentTypeClassifierResolver = documentTypeClassifier.DocumentTypeClassifierResolverId;

        this.documentConfigsToDisplay = this.documentConfigs.filter(x=> x.DistributionObjectId === this.selectedDocumentType);

        const documentTypeClassifierResolver = this.documentTypeClassifierResolvers.filter(x=>x.Id === documentTypeClassifier.DocumentTypeClassifierResolverId)[0];
        if (documentTypeClassifierResolver) {
            this.documentTypeClassifierResolverDataFields = await this.developerService.getDocumentTypeClassifierResolverDataFieldGroups(documentTypeClassifierResolver.ArtifactId, documentTypeClassifier.Id);
        }

        const controlMessageInResolverArtifact = this.artifacts.filter(x=>x.ArtifactId === documentTypeClassifier.ControlMessageInResolverArtifactId)[0];
        if (controlMessageInResolverArtifact) {
            this.controlMessageInResolverDataFields = await this.developerService.getControlMessageInResolverDataFieldGroups(controlMessageInResolverArtifact.ArtifactId, documentTypeClassifier.Id);
        }

        // Value required for validation are reset in order to correctly access them during the onRowValidation event.
        e.component.cellValue(e.component.getRowIndexByKey(e.key), 'Name', e.component.cellValue(e.component.getRowIndexByKey(e.key), 'Name'));
        e.component.cellValue(e.component.getRowIndexByKey(e.key), 'DocumentTypeId', e.component.cellValue(e.component.getRowIndexByKey(e.key), 'DocumentTypeId'));
        e.component.cellValue(e.component.getRowIndexByKey(e.key), 'DocumentTypeClassifierResolverId', e.component.cellValue(e.component.getRowIndexByKey(e.key), 'DocumentTypeClassifierResolverId'));
        e.component.cellValue(e.component.getRowIndexByKey(e.key), 'IsControlMessageInFlow', e.component.cellValue(e.component.getRowIndexByKey(e.key), 'IsControlMessageInFlow'));
        e.component.cellValue(e.component.getRowIndexByKey(e.key), 'ControlMessageInResolverArtifactId', e.component.cellValue(e.component.getRowIndexByKey(e.key), 'ControlMessageInResolverArtifactId'));
        //e.component.cellValue(e.component.getRowIndexByKey(e.key), "DocumentConfigId", e.component.cellValue(e.component.getRowIndexByKey(e.key), "DocumentConfigId"));
    }

    onEditorPreparing(e: any) {
        if (e.dataField === 'IsControlMessageInFlow' && e.parentType === 'dataRow') {
            this.disableControlMessageInFlowOptions = !e.row.data.IsControlMessageInFlow;
            e.editorOptions.onValueChanged = function(ev: any) {
                const selectedValue = ev.value;
                this.disableControlMessageInFlowOptions = !selectedValue;
                this.clearArtifactPickerCurrentSelection = !selectedValue;
                e.setValue(selectedValue);
            }.bind(this);
        }
    }

    onRowValidating(e: any) {
        let isValid = true;
        const documentTypeClassifier = e.newData as DocumentTypeClassifier;

        this.documentTypeClassifierResolverFormGroup.updateValueAndValidity();
        this.controlMessageInResolverFormGroup.updateValueAndValidity();

        if (documentTypeClassifier.Name.length === 0) {
            isValid = false;
        } else if (documentTypeClassifier.DocumentTypeId === 0) {
            isValid = false;
        } else if (documentTypeClassifier.DocumentTypeClassifierResolverId === 0) {
            isValid = false;
        } else if (documentTypeClassifier.DocumentTypeClassifierResolverId !== 0 && !this.documentTypeClassifierResolverFormGroup.valid) {
            isValid = false;
        } else if (documentTypeClassifier.IsControlMessageInFlow && (documentTypeClassifier.ControlMessageInResolverArtifactId === '' || !this.controlMessageInResolverFormGroup.valid)) {
            isValid = false;
        }

        e.isValid = isValid;
    }

    getDocumentTypeClassifierResolverName(documentTypeClassifier: DocumentTypeClassifier): string {
        return this.documentTypeClassifierResolvers.filter(x=>x.Id === documentTypeClassifier.DocumentTypeClassifierResolverId)[0]?.Name;
    }

    getDocumentTypeName(documentTypeClassifier: DocumentTypeClassifier): string {
        return this.documentTypes.filter(x=>x.Id === documentTypeClassifier.DocumentTypeId)[0]?.Name;
    }

    getIdentificationTypeName(documentTypeClassifier: DocumentTypeClassifier): string {
        return this.identificationTypes.filter(x=>x.IdentificationTypeId === documentTypeClassifier.IdentificationTypeId)[0]?.Name;
    }

    async onDocumentTypeClassifierResolverChanged(id: number, cellInfo: any) {
        cellInfo.setValue(id);

        const documentTypeClassifierResolver = this.documentTypeClassifierResolvers.filter(x=>x.Id === id)[0];
        if (documentTypeClassifierResolver) {
            this.documentTypeClassifierResolverDataFields = await this.developerService.getDocumentTypeClassifierResolverDataFieldGroups(documentTypeClassifierResolver.ArtifactId, cellInfo.row.data.Id);
        } else {
            this.documentTypeClassifierResolverDataFields = [];
        }
    }

    async onControlMessageInResolverChanged(id: string | null, cellInfo: any) {
        if (id === undefined) {
            id = null;
        }

        cellInfo.setValue(id);

        const controlMessageInResolverArtifact = this.artifacts.filter(x=>x.ArtifactId === id)[0];
        if (controlMessageInResolverArtifact) {
            this.controlMessageInResolverDataFields = await this.developerService.getControlMessageInResolverDataFieldGroups(controlMessageInResolverArtifact.ArtifactId, cellInfo.row.data.Id);
        } else {
            this.controlMessageInResolverDataFields = [];
        }
    }

    setDocumentConfigValue(newData: any, value: any, currentRowData: any) {
        if (value !== currentRowData.DocumentTypeId) {
            newData.DocumentTypeId = value;
            newData.DocumentConfigId = null;
            this.selectedDocumentType = value;
            this.selectedDocumentTypeClassifierResolver = currentRowData.DocumentTypeClassifierResolverId;
            this.selectedIdentificationType = currentRowData.IdentificationTypeId;
            this.documentConfigsToDisplay = this.documentConfigs.filter(x=> x.DistributionObjectId === value);
        }
    }

    onGenericValueChanged(id: any, cellInfo: any) {
        cellInfo.setValue(id);
    }

    getDocumentConfigName(documentTypeClassifier: DocumentTypeClassifier): string {
        const documentConfig = this.documentConfigs.filter(x=>x.DocumentConfigId === documentTypeClassifier.DocumentConfigId)[0];
        if (documentConfig) {
            return `${documentConfig.FormatName} / ${documentConfig.VariantName} / ${documentConfig.VersionName}`;
        } else {
            return '';
        }
    }

     onInitNewRow(e: DxDataGridTypes.InitNewRowEvent<DocumentTypeClassifier>): void {
        e.data = {
            Id: null,
            Name: '',
            DocumentTypeClassifierResolverId: 0,
            DocumentTypeId: 0,
            DocumentTypeClassifierResolverConfiguration: '',
            IdentificationTypeId: null,
            DocumentConfigId: null,
            IsControlMessageInFlow: false,
            ControlMessageInResolverArtifactId: '',
            ControlMessageInResolverConfiguration: '' } as DocumentTypeClassifier;

            this.selectedDocumentTypeClassifierResolver = 0;
            this.selectedDocumentType = 0;
            this.selectedDocumentConfig = 0;
            this.selectedIdentificationType = 0;
            this.documentTypeClassifierResolverDataFields = [];
            this.controlMessageInResolverDataFields = [];
            this.documentConfigsToDisplay = [];
    }

    onRowInserting = (e: DxDataGridTypes.RowInsertingEvent<DocumentTypeClassifier>): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data, item => this.saveDocumentTypeClassifier(item));
    };

    onRowUpdating = (e: DxDataGridTypes.RowUpdatingEvent<DocumentTypeClassifier>): void => {
        e.cancel = this.gridService.handleDataGridRow({ ...e.oldData, ...e.newData }, item => this.saveDocumentTypeClassifier(item), e.newData);
    };

    onRowRemoving = (e: DxDataGridTypes.RowRemovingEvent<DocumentTypeClassifier>): void => {
        e.cancel = this.gridService.handleDataGridRow(e.data, item => this.removeDocumentTypeClassifier(item));
    };

    async saveDocumentTypeClassifier(item: DocumentTypeClassifier): Promise<DocumentTypeClassifier> {
        const documentTypeClassifierResolverDatafieldValues = DataFieldGroup.getDataFieldValues(this.documentTypeClassifierResolverDataFields, false);
        const controlMessageInResolverDatafieldValues = DataFieldGroup.getDataFieldValues(this.controlMessageInResolverDataFields, false);

        if (item.ControlMessageInResolverArtifactId === '') {
            item.ControlMessageInResolverArtifactId = null;
        }

        const savedItem = await this.developerService.saveDocumentTypeClassifier(item, documentTypeClassifierResolverDatafieldValues, controlMessageInResolverDatafieldValues);
        this.toastService.showToast('Document type classifier saved...', ToastType.success);
        this.documentTypeClassifierResolverDataFields = [];
        return savedItem;
    }

    async removeDocumentTypeClassifier(item: DocumentTypeClassifier): Promise<DocumentTypeClassifier> {
        await this.developerService.deleteDocumentTypeClassifier(item.Id);
        this.toastService.showToast('Document type classifier deleted...', ToastType.success);
        return item;
    }
}
